/**
 * PublicScanPage — Zone QR scan page for officer/supervisor use.
 * Accessed via: https://<app>/?z=<zoneQrHash>
 *
 * Flow: zone QR scanned → officer sees zone context → scans employee badge
 *       (or types ID manually) → selects zone action → submits.
 */
import React, { useState, useEffect, useRef } from 'react';
import { CheckCircle, AlertTriangle, LogIn, LogOut, Stethoscope, Flame, Lock, HelpCircle, Shield, ArrowLeft, ScanLine, X } from 'lucide-react';
import jsQR from 'jsqr';

const API = process.env.REACT_APP_API_URL || 'http://localhost:4000';

const S = {
  bg: '#0a0e17', surface: '#131a2b', elevated: '#1a2235',
  border: 'rgba(255,255,255,0.08)', text: '#e2e8f0', muted: '#64748b',
  subtle: '#94a3b8', green: '#22c55e', red: '#ef4444', blue: '#3b82f6',
  amber: '#f59e0b', orange: '#f97316', pink: '#ec4899', purple: '#a78bfa',
};

// ── Badge camera scanner (getUserMedia + jsQR — no DOM-manipulating library) ──
function BadgeScanner({ onDetected, onCancel }) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const rafRef = useRef(null);
  const doneRef = useRef(false); // prevents double-fire on detect or cancel

  useEffect(() => {
    let stream = null;

    function stopStream() {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
      if (stream) { stream.getTracks().forEach(t => t.stop()); stream = null; }
    }

    function scan() {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      if (!video || !canvas || doneRef.current) return;
      if (video.readyState < video.HAVE_ENOUGH_DATA) {
        rafRef.current = requestAnimationFrame(scan);
        return;
      }
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext('2d', { willReadFrequently: true });
      ctx.drawImage(video, 0, 0);
      const img = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const code = jsQR(img.data, img.width, img.height, { inversionAttempts: 'dontInvert' });
      if (code && code.data) {
        doneRef.current = true;
        stopStream();
        onDetected(code.data);
        return;
      }
      rafRef.current = requestAnimationFrame(scan);
    }

    navigator.mediaDevices
      .getUserMedia({ video: { facingMode: { ideal: 'environment' } } })
      .then(s => {
        stream = s;
        if (doneRef.current) { stopStream(); return; } // cancelled before camera ready
        const video = videoRef.current;
        if (!video) { stopStream(); return; }
        video.srcObject = s;
        video.play().then(() => { rafRef.current = requestAnimationFrame(scan); }).catch(() => {});
      })
      .catch(() => {}); // permission denied — user can tap Cancel

    return () => {
      if (!doneRef.current) stopStream();
    };
  }, []);

  function handleCancel() {
    doneRef.current = true;
    onCancel();
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: '#000', zIndex: 999, display: 'flex', flexDirection: 'column' }}>
      <div style={{ flex: 1, position: 'relative', overflow: 'hidden' }}>
        <video ref={videoRef} playsInline muted style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        <canvas ref={canvasRef} style={{ display: 'none' }} />
        {/* Corner brackets overlay */}
        <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
          <div style={{ width: '220px', height: '220px', position: 'relative' }}>
            {[
              { top: 0, left: 0, borderTop: `3px solid ${S.blue}`, borderLeft: `3px solid ${S.blue}`, borderRadius: '4px 0 0 0' },
              { top: 0, right: 0, borderTop: `3px solid ${S.blue}`, borderRight: `3px solid ${S.blue}`, borderRadius: '0 4px 0 0' },
              { bottom: 0, left: 0, borderBottom: `3px solid ${S.blue}`, borderLeft: `3px solid ${S.blue}`, borderRadius: '0 0 0 4px' },
              { bottom: 0, right: 0, borderBottom: `3px solid ${S.blue}`, borderRight: `3px solid ${S.blue}`, borderRadius: '0 0 4px 0' },
            ].map((style, i) => (
              <div key={i} style={{ position: 'absolute', width: '28px', height: '28px', ...style }} />
            ))}
          </div>
        </div>
      </div>
      <div style={{ background: S.surface, padding: '20px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px' }}>
        <div style={{ fontSize: '14px', color: S.subtle, textAlign: 'center' }}>Point at the employee or visitor badge QR code</div>
        <button onClick={handleCancel} style={{ padding: '12px 32px', borderRadius: '10px', border: `1px solid ${S.border}`, background: S.elevated, color: S.subtle, fontSize: '14px', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
          Cancel
        </button>
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────
export default function PublicScanPage({ qrHash }) {
  const [phase, setPhase] = useState('loading'); // loading | enter_id | scanning | action | emergency | done | error
  const [zoneInfo, setZoneInfo] = useState(null);
  const [employeeIdCode, setEmployeeIdCode] = useState('');
  const [employeeName, setEmployeeName] = useState('');
  const [isVisitor, setIsVisitor] = useState(false);
  const [visitorCardHash, setVisitorCardHash] = useState('');
  const [idError, setIdError] = useState('');
  const [action, setAction] = useState(null);
  const [gps, setGps] = useState(null);
  const [gpsLoading, setGpsLoading] = useState(false);
  const gpsFiredRef = useRef(false);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [loadError, setLoadError] = useState('');

  useEffect(() => {
    fetch(`${API}/api/public/zone/${qrHash}`)
      .then(r => r.json())
      .then(data => {
        if (data.error) { setLoadError(data.error); setPhase('error'); return; }
        setZoneInfo(data);
        setPhase('enter_id');
        // Start capturing device GPS immediately — will be ready by the time officer submits
        if (!gpsFiredRef.current && navigator.geolocation) {
          gpsFiredRef.current = true;
          setGpsLoading(true);
          navigator.geolocation.getCurrentPosition(
            pos => { setGps({ lat: pos.coords.latitude, lng: pos.coords.longitude }); setGpsLoading(false); },
            () => setGpsLoading(false),
            { timeout: 8000 }
          );
        }
      })
      .catch(() => { setLoadError('Unable to reach the server.'); setPhase('error'); });
  }, [qrHash]);

  // Called when camera decodes a QR from a badge (employee or visitor card)
  async function handleBadgeScanned(decoded) {
    setPhase('enter_id');
    try {
      const url = new URL(decoded);
      // Employee badge: ?e=HASH
      const empHash = url.searchParams.get('e');
      if (empHash) {
        const r = await fetch(`${API}/api/public/employee/${empHash}`);
        const data = await r.json();
        if (data.error) { setIdError('Badge not recognised. Try typing the ID.'); return; }
        setEmployeeIdCode(data.employeeId); setEmployeeName(data.name);
        setIsVisitor(false); setVisitorCardHash('');
        setIdError(''); setPhase('action');
        return;
      }
      // Visitor card: ?vis=HASH
      const visHash = url.searchParams.get('vis');
      if (visHash) {
        const r = await fetch(`${API}/api/public/visitor-card/${visHash}`);
        const data = await r.json();
        if (data.error) { setIdError(data.error || 'Visitor card not recognised.'); return; }
        setVisitorCardHash(visHash);
        setEmployeeIdCode(data.cardNumber);
        setEmployeeName(data.visitorName || data.cardNumber);
        setIsVisitor(true); setIdError(''); setPhase('action');
        return;
      }
    } catch (_) {}
    // Fallback: treat as employee ID
    setEmployeeIdCode(decoded.trim().toUpperCase()); setEmployeeName('');
    setIsVisitor(false); setVisitorCardHash('');
    setIdError(''); setPhase('action');
  }

  function handleIdSubmit(e) {
    e.preventDefault();
    const code = employeeIdCode.trim().toUpperCase();
    if (!code) { setIdError('Please enter the employee ID'); return; }
    setIdError('');
    setEmployeeName('');
    setPhase('action');
  }

  function handleAction(act) {
    setAction(act);
    if (act === 'NEED_HELP') {
      setPhase('emergency'); // emergency phase passes gps to submitScan on type selection
    } else {
      submitScan(act, null, gps); // gps = device GPS captured on load, or null
    }
  }

  function submitScan(act, emergencyType, gpsData) {
    setSubmitting(true);
    const body = isVisitor
      ? { qrHash, visitorCardHash, action: act, emergencyType: emergencyType || undefined, gps: gpsData || undefined }
      : { qrHash, employeeIdCode: employeeIdCode.trim().toUpperCase(), action: act, emergencyType: emergencyType || undefined, gps: gpsData || undefined };
    fetch(`${API}/api/public/scan`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })
      .then(r => r.json())
      .then(data => {
        setSubmitting(false);
        if (data.error) { setIdError(data.error); setPhase('action'); return; }
        setResult(data);
        setPhase('done');
      })
      .catch(() => { setSubmitting(false); setIdError('Network error. Please try again.'); });
  }

  const page = { minHeight: '100vh', background: S.bg, display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '0 0 40px', fontFamily: "'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif" };
  const header = { width: '100%', background: S.surface, borderBottom: `1px solid ${S.border}`, padding: '14px 20px', display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '24px' };
  const card = { width: '100%', maxWidth: '420px', padding: '0 20px' };
  const bigBtn = (color) => ({ width: '100%', padding: '20px 16px', borderRadius: '14px', border: 'none', background: color, color: '#fff', fontSize: '18px', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px', marginBottom: '12px', boxShadow: `0 4px 20px ${color}44` });
  const smBtn = (color) => ({ flex: 1, padding: '14px 10px', borderRadius: '12px', border: `1px solid ${color}44`, background: color + '18', color, fontSize: '14px', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' });

  if (phase === 'loading') return (
    <div style={page}>
      <div style={{ marginTop: '40vh', color: S.muted, fontSize: '14px', textAlign: 'center' }}>
        <Shield size={32} color={S.blue} style={{ display: 'block', margin: '0 auto 12px' }} />Loading zone…
      </div>
    </div>
  );

  if (phase === 'error') return (
    <div style={page}>
      <div style={{ marginTop: '30vh', padding: '32px', textAlign: 'center', maxWidth: '360px' }}>
        <AlertTriangle size={40} color={S.amber} style={{ display: 'block', margin: '0 auto 16px' }} />
        <div style={{ fontSize: '16px', fontWeight: 700, color: S.text, marginBottom: '8px' }}>QR Code Error</div>
        <div style={{ fontSize: '14px', color: S.subtle }}>{loadError}</div>
      </div>
    </div>
  );

  if (phase === 'scanning') return (
    <BadgeScanner onDetected={handleBadgeScanned} onCancel={() => setPhase('enter_id')} />
  );

  const zone = zoneInfo?.zone;
  const site = zoneInfo?.site;
  const company = zoneInfo?.company;

  return (
    <div style={page}>
      <div style={header}>
        <Shield size={20} color={S.blue} />
        <div>
          <div style={{ fontSize: '13px', fontWeight: 700, color: S.text }}>SafeCheck</div>
          <div style={{ fontSize: '11px', color: S.muted }}>Zone Access</div>
        </div>
      </div>

      <div style={card}>
        {/* Zone context */}
        <div style={{ background: S.surface, border: `1px solid ${S.border}`, borderRadius: '14px', padding: '16px 18px', marginBottom: '24px' }}>
          <div style={{ fontSize: '11px', color: S.muted, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '6px' }}>Zone</div>
          <div style={{ fontSize: '18px', fontWeight: 700, color: S.text, marginBottom: '4px' }}>{zone?.name}</div>
          <div style={{ fontSize: '13px', color: S.subtle }}>{site?.name} · Floor {zone?.floor} · {company?.name}</div>
        </div>

        {/* Identify employee */}
        {phase === 'enter_id' && (
          <div>
            <div style={{ fontSize: '14px', fontWeight: 600, color: S.text, marginBottom: '16px' }}>Scan employee or visitor badge</div>

            <button onClick={() => setPhase('scanning')} style={{ width: '100%', padding: '20px 16px', borderRadius: '14px', border: `2px solid ${S.blue}44`, background: S.blue+'18', color: S.blue, fontSize: '17px', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px', marginBottom: '16px' }}>
              <ScanLine size={24} />Scan Badge
            </button>

            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
              <div style={{ flex: 1, height: '1px', background: S.border }} />
              <span style={{ fontSize: '12px', color: S.muted }}>or type ID manually</span>
              <div style={{ flex: 1, height: '1px', background: S.border }} />
            </div>

            <form onSubmit={handleIdSubmit}>
              <input value={employeeIdCode} onChange={e => setEmployeeIdCode(e.target.value.toUpperCase())} placeholder="e.g. EMP-001" autoCapitalize="characters"
                style={{ width: '100%', background: S.elevated, border: `1px solid ${idError ? S.red : S.border}`, borderRadius: '10px', padding: '14px 16px', color: S.text, fontSize: '16px', fontFamily: 'JetBrains Mono, monospace', outline: 'none', marginBottom: '8px' }} />
              {idError && <div style={{ fontSize: '13px', color: S.red, marginBottom: '10px' }}>{idError}</div>}
              <button type="submit" style={{ width: '100%', padding: '14px', borderRadius: '10px', border: 'none', background: S.blue, color: '#fff', fontSize: '15px', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>Continue →</button>
            </form>
          </div>
        )}

        {/* Action selection */}
        {phase === 'action' && (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '20px' }}>
              <button onClick={() => { setPhase('enter_id'); setAction(null); setIdError(''); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: S.muted, padding: 0, display: 'flex' }}>
                <ArrowLeft size={16} />
              </button>
              <div style={{ fontSize: '13px', color: S.subtle }}>
                {employeeName
                  ? <><span style={{ color: S.text, fontWeight: 600 }}>{employeeName}</span> <span style={{ fontFamily: 'JetBrains Mono, monospace' }}>({employeeIdCode})</span></>
                  : <span style={{ fontFamily: 'JetBrains Mono, monospace', color: S.text }}>{employeeIdCode}</span>
                }
              </div>
            </div>

            {idError && <div style={{ background: S.red+'22', border: `1px solid ${S.red}44`, borderRadius: '10px', padding: '12px 14px', marginBottom: '16px', fontSize: '13px', color: S.red }}>{idError}</div>}

            <div style={{ fontSize: '14px', fontWeight: 600, color: S.text, marginBottom: '16px' }}>Select action</div>

            <button onClick={() => handleAction('SAFE')} disabled={submitting} style={bigBtn(S.green)}>
              <CheckCircle size={26} /> SAFE
            </button>
            <button onClick={() => handleAction('NEED_HELP')} disabled={submitting} style={{ ...bigBtn(S.red), marginBottom: '20px' }}>
              <AlertTriangle size={26} /> NEED HELP
            </button>

            <div style={{ borderTop: `1px solid ${S.border}`, paddingTop: '16px', marginBottom: '10px' }}>
              <div style={{ fontSize: '12px', color: S.muted, marginBottom: '10px', textAlign: 'center' }}>Zone access</div>
              <div style={{ display: 'flex', gap: '10px' }}>
                <button onClick={() => handleAction('ENTER_ZONE')} disabled={submitting} style={smBtn(S.blue)}><LogIn size={20} />Enter Zone</button>
                <button onClick={() => handleAction('EXIT_SITE')} disabled={submitting} style={smBtn(S.subtle)}><LogOut size={20} />Exit Site</button>
              </div>
            </div>
            {submitting && <div style={{ textAlign: 'center', color: S.muted, fontSize: '13px', marginTop: '12px' }}>Submitting…</div>}
          </div>
        )}

        {/* Emergency type */}
        {phase === 'emergency' && (
          <div>
            <div style={{ textAlign: 'center', marginBottom: '24px' }}>
              <AlertTriangle size={44} color={S.red} style={{ display: 'block', margin: '0 auto 12px' }} />
              <div style={{ fontSize: '20px', fontWeight: 700, color: S.red, marginBottom: '6px' }}>Emergency Alert</div>
              <div style={{ fontSize: '14px', color: S.subtle }}>Select the type of emergency</div>
              {gpsLoading && <div style={{ fontSize: '12px', color: S.blue, marginTop: '8px' }}>Getting GPS…</div>}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              {[['MEDICAL','Medical',S.pink,Stethoscope],['FIRE','Fire',S.orange,Flame],['SECURITY','Security',S.purple,Lock],['OTHER','Other',S.amber,HelpCircle]].map(([type,label,color,Icon]) => (
                <button key={type} onClick={() => submitScan('NEED_HELP', type, gps)} disabled={submitting}
                  style={{ padding: '22px 10px', borderRadius: '14px', border: `2px solid ${color}`, background: color+'22', color, fontSize: '15px', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px' }}>
                  <Icon size={28} />{label}
                </button>
              ))}
            </div>
            {submitting && <div style={{ textAlign: 'center', color: S.muted, fontSize: '13px', marginTop: '16px' }}>Sending alert…</div>}
          </div>
        )}

        {/* Done */}
        {phase === 'done' && (
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <CheckCircle size={64} color={result?.action === 'NEED_HELP' ? S.red : S.green} style={{ display: 'block', margin: '0 auto 16px' }} />
            <div style={{ fontSize: '22px', fontWeight: 700, color: S.text, marginBottom: '8px' }}>
              {result?.action === 'NEED_HELP' ? '🚨 Alert Sent' : result?.action === 'ENTER_ZONE' ? '✓ Checked In' : result?.action === 'EXIT_SITE' ? '✓ Checked Out' : '✓ Marked Safe'}
            </div>
            <div style={{ fontSize: '15px', color: S.subtle, marginBottom: '4px' }}>{result?.isVisitor ? result?.visitorName : result?.employeeName}</div>
            <div style={{ fontSize: '13px', color: S.muted, marginBottom: '24px' }}>{result?.zoneName} · {result?.siteName}</div>
            {result?.action === 'NEED_HELP' && (
              <div style={{ background: S.red+'22', border: `1px solid ${S.red}44`, borderRadius: '12px', padding: '14px', marginBottom: '20px', fontSize: '13px', color: S.red, fontWeight: 600 }}>
                Emergency alert sent to the safety team.
              </div>
            )}
            <button onClick={() => { setPhase('enter_id'); setAction(null); setResult(null); setIdError(''); setEmployeeIdCode(''); setEmployeeName(''); setIsVisitor(false); setVisitorCardHash(''); }}
              style={{ padding: '12px 32px', borderRadius: '10px', border: `1px solid ${S.border}`, background: S.elevated, color: S.subtle, fontSize: '14px', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
              Next Person
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
